/**
 * PriceFilterPopover
 * Reusable price filter with toggle chips (All/Free/Paid/Donation-based),
 * range slider ($0-$100), and APPLY/Reset. Matches Search page Figma design.
 */

import { useCallback, useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Slider } from "@/components/ui/slider"
import { FilterChipButton } from "./FilterChipButton"
import { PopoverFooter } from "./PopoverFooter"
import { ToggleChip } from "./ToggleChip"

const PRICE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "free", label: "Free" },
  { value: "paid", label: "Paid" },
  { value: "donation", label: "Donation" },
]

interface PriceFilterState {
  price: string[]
  priceRange: [number, number]
}

interface PriceFilterPopoverProps {
  /** Currently committed price chip selections */
  selectedPrice: string[]
  /** Currently committed price range [min, max] */
  priceRange: [number, number]
  /** Called when user clicks APPLY */
  onApply: (price: string[], priceRange: [number, number]) => void
}

export function PriceFilterPopover({
  selectedPrice,
  priceRange,
  onApply,
}: PriceFilterPopoverProps) {
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState<PriceFilterState>({
    price: selectedPrice,
    priceRange,
  })

  const handleOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) setLocal({ price: selectedPrice, priceRange })
      setOpen(nextOpen)
    },
    [selectedPrice, priceRange],
  )

  const displayValue = () => {
    if (selectedPrice.length === 0 || selectedPrice.includes("all")) return undefined
    if (selectedPrice.length === 1) {
      const label = PRICE_OPTIONS.find(o => o.value === selectedPrice[0])?.label
      return label
    }
    return String(selectedPrice.length)
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <FilterChipButton
          label={`Price${displayValue() ? ":" : ""}`}
          value={displayValue()}
          isActive={open || !!displayValue()}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-auto bg-popover border border-border rounded-[20px] p-[20px]"
      >
        <div className="flex flex-col gap-[20px]">
          {/* Price type chips */}
          <div className="flex items-center gap-[10px]">
            {PRICE_OPTIONS.map(option => (
              <ToggleChip
                key={option.value}
                label={option.label}
                selected={local.price.includes(option.value)}
                onToggle={() => {
                  if (option.value === "all") {
                    setLocal(prev => ({ ...prev, price: ["all"] }))
                  } else {
                    setLocal(prev => ({
                      ...prev,
                      price: prev.price.includes(option.value)
                        ? prev.price.filter(v => v !== option.value)
                        : [option.value],
                    }))
                  }
                }}
              />
            ))}
          </div>

          {/* Range slider */}
          <Slider
            value={local.priceRange}
            onValueChange={val =>
              setLocal(prev => ({ ...prev, priceRange: val as [number, number] }))
            }
            min={0}
            max={100}
            step={1}
            className="w-[300px] [&_[data-slot=slider-track]]:h-[3px] [&_[data-slot=slider-track]]:bg-connect-glass-border [&_[data-slot=slider-range]]:bg-brand-olive [&_[data-slot=slider-thumb]]:size-[15px] [&_[data-slot=slider-thumb]]:border-brand-olive [&_[data-slot=slider-thumb]]:bg-brand-olive"
          />
          <div className="flex justify-between w-[300px]">
            <div className="flex flex-col gap-[5px] border-b border-text-primary/20 pb-[10px] w-[120px]">
              <span className="text-[12px] font-normal text-text-primary opacity-50 uppercase">
                Min
              </span>
              <span className="text-[14px] font-semibold text-text-primary">
                ${local.priceRange[0]}
              </span>
            </div>
            <div className="flex flex-col gap-[5px] border-b border-text-primary/20 pb-[10px] w-[120px]">
              <span className="text-[12px] font-normal text-text-primary opacity-50 uppercase">
                Max
              </span>
              <span className="text-[14px] font-semibold text-text-primary">
                ${local.priceRange[1]}
              </span>
            </div>
          </div>

          <PopoverFooter
            onApply={() => {
              onApply(local.price, local.priceRange)
              setOpen(false)
            }}
            onReset={() => setLocal({ price: [], priceRange: [0, 100] })}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
